/**
 * Flight Validation Utilities
 * Validation functions for flight details in bid generation
 */

/**
 * Validates a single flight leg
 * @param {Object} flight - Flight leg data
 * @returns {Object} - { errors: {}, isValid: boolean }
 */
export const validateFlightLeg = (flight) => {
  const errors = {};

  // Validate departure - accept both object format and string format
  const hasDeparture = flight?.departure && (
    (typeof flight.departure === 'object' && flight.departure.code) ||
    (typeof flight.departure === 'string' && flight.departure.trim() !== '')
  );
  
  if (!hasDeparture) {
    errors.departure = 'Departure airport is required';
  }

  // Validate arrival - accept both object format and string format
  const hasArrival = flight?.arrival && (
    (typeof flight.arrival === 'object' && flight.arrival.code) ||
    (typeof flight.arrival === 'string' && flight.arrival.trim() !== '')
  );
  
  if (!hasArrival) {
    errors.arrival = 'Arrival airport is required';
  }

  // Validate that arrival and departure are different
  if (hasDeparture && hasArrival) {
    const departureCode = typeof flight.departure === 'object' ? flight.departure.code : flight.departure;
    const arrivalCode = typeof flight.arrival === 'object' ? flight.arrival.code : flight.arrival;
    
    if (departureCode === arrivalCode) {
      errors.arrival = 'Arrival must be different from departure';
    }
  }

  // Validate departure date
  if (!flight?.departureDate) {
    errors.departureDate = 'Departure date is required';
  } else {
    const departureDate = new Date(flight.departureDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (departureDate < today) {
      errors.departureDate = 'Departure date cannot be in the past';
    }
  }

  // Validate return date (if provided)
  if (flight?.returnDate) {
    const returnDate = new Date(flight.returnDate);
    const departureDate = new Date(flight.departureDate);

    if (returnDate <= departureDate) {
      errors.returnDate = 'Return date must be after departure date';
    }
  }

  // Validate passenger count
  if (!flight?.passengerCount) {
    errors.passengerCount = 'Passenger count is required';
  } else {
    const count = parseInt(flight.passengerCount, 10);
    if (isNaN(count) || count < 1 || count > 50) {
      errors.passengerCount = 'Passenger count must be between 1 and 50';
    }
  }

  return {
    errors,
    isValid: Object.keys(errors).length === 0
  };
};

/**
 * Validates all flight legs based on flight type
 * @param {Array} flights - Array of flight leg objects
 * @param {string} flightType - 'one-way', 'round-trip', or 'multi-leg'
 * @returns {Object} - { errors: Array<Object>, isValid: boolean }
 */
export const validateFlightDetails = (flights, flightType) => {
  if (!flights || !Array.isArray(flights) || flights.length === 0) {
    return {
      errors: [{ general: 'At least one flight leg is required' }],
      isValid: false
    };
  }

  // Validate based on flight type
  if (flightType === 'one-way' && flights.length !== 1) {
    return {
      errors: [{ general: 'One-way flights must have exactly one leg' }],
      isValid: false
    };
  }

  if (flightType === 'round-trip') {
    // Round-trip can be either 1 leg with returnDate or 2 legs (outbound + return)
    if (flights.length === 1 && !flights[0].returnDate) {
      return {
        errors: [{ returnDate: 'Return date is required for round-trip flights' }],
        isValid: false
      };
    }

    if (flights.length !== 1 && flights.length !== 2) {
      return {
        errors: [{ general: 'Round-trip flights must have one or two legs' }],
        isValid: false
      };
    }
  }

  if (flightType === 'multi-leg' && flights.length < 2) {
    return {
      errors: [{ general: 'Multi-leg flights must have at least two legs' }],
      isValid: false
    };
  }

  // Validate each flight leg
  const allErrors = flights.map(flight => {
    const validation = validateFlightLeg(flight);
    return validation.errors;
  });

  const hasErrors = allErrors.some(errors => Object.keys(errors).length > 0);

  return {
    errors: allErrors,
    isValid: !hasErrors
  };
};

/**
 * Check if flight details section is complete
 * @param {Object} data - Flight details data { type, flights }
 * @returns {boolean}
 */
export const isFlightDetailsComplete = (data) => {
  if (!data || !data.type || !data.flights) {
    return false;
  }

  const validation = validateFlightDetails(data.flights, data.type);
  return validation.isValid;
};
